import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function EconomicDataAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Economic Calendar - Calendar API" description="Query economic calendar data via the Calendar API; request parameters, code examples and the response schema for the economic calendar endpoint."  canonicalUrl="https://axionquant.com/docs/04-macros-and-signals/calendar/economic-calendar" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Economic Calendar - Calendar API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>
        {/* Economic Calendar */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /econ/calendar
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves a filtered list of economic calendar events including economic releases, central bank meetings, holidays, and important announcements. Supports comprehensive filtering by date range, country, importance, currency, and category.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">from</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Start date filter (YYYY-MM-DD or ISO 8601)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">to</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        End date filter (YYYY-MM-DD or ISO 8601)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">country</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Country code(s) (single or comma-separated, e.g., &quot;US&quot; or &quot;US,GB,JP&quot;)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">minImportance</td>
                      <td className="px-4 py-3 text-sm text-gray-700">number</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Minimum importance level (-1 to 3, where 3 is most important)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">currency</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Currency code(s) (single or comma-separated, e.g., &quot;USD&quot; or &quot;USD,EUR,GBP&quot;)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">category</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Category code(s) (single or comma-separated, e.g., &quot;gov&quot; or &quot;gov,fin,mfg&quot;)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">limit</td>
                      <td className="px-4 py-3 text-sm text-gray-700">number</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Optional
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Controls the number of results returned, default is 10
                      </td>
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/econ/calendar', '/econ/calendar?from=2024-01-01&to=2024-12-31']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Enum Values</h3>
              <div className="space-y-6">

                {/* Country */}
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                    <code className="text-sm font-mono font-semibold text-gray-800">country</code>
                    <span className="text-xs text-gray-500">- accepted values</span>
                  </div>
                  <div className="p-4 flex flex-wrap gap-2">
                    {[
                      { value: "america", flag: "🇺🇸" },
                      { value: "australia", flag: "🇦🇺" },
                      { value: "canada", flag: "🇨🇦" },
                      { value: "china", flag: "🇨🇳" },
                      { value: "germany", flag: "🇩🇪" },
                      { value: "hongkong", flag: "🇭🇰" },
                      { value: "india", flag: "🇮🇳" },
                      { value: "japan", flag: "🇯🇵" },
                      { value: "korea", flag: "🇰🇷" },
                      { value: "ksa", flag: "🇸🇦" },
                      { value: "mexico", flag: "🇲🇽" },
                      { value: "singapore", flag: "🇸🇬" },
                      { value: "uk", flag: "🇬🇧" },
                    ].map(({ value, flag }) => (
                        <span key={value} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-gray-200 text-sm font-mono text-gray-700 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                          <span className="text-base leading-none">{flag}</span>
                          {value}
          </span>
        ))}
      </div>
    </div>

    {/* Currency */}
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
        <code className="text-sm font-mono font-semibold text-gray-800">currency</code>
        <span className="text-xs text-gray-500">- accepted values</span>
      </div>
      <div className="p-4 flex flex-wrap gap-2">
        {[
          { code: "ARS", name: "Argentine Peso" },
          { code: "AUD", name: "Australian Dollar" },
          { code: "BRL", name: "Brazilian Real" },
          { code: "CAD", name: "Canadian Dollar" },
          { code: "CNY", name: "Chinese Yuan" },
          { code: "EUR", name: "Euro" },
          { code: "GBP", name: "British Pound" },
          { code: "HKD", name: "Hong Kong Dollar" },
          { code: "IDR", name: "Indonesian Rupiah" },
          { code: "INR", name: "Indian Rupee" },
          { code: "JPY", name: "Japanese Yen" },
          { code: "KRW", name: "South Korean Won" },
          { code: "MXN", name: "Mexican Peso" },
          { code: "RUB", name: "Russian Ruble" },
          { code: "SAR", name: "Saudi Riyal" },
          { code: "SGD", name: "Singapore Dollar" },
          { code: "TRY", name: "Turkish Lira" },
          { code: "USD", name: "US Dollar" },
          { code: "ZAR", name: "South African Rand" },
        ].map(({ code, name }) => (
          <span key={code} title={name} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors cursor-default">
            <span className="text-sm font-mono font-semibold text-gray-800">{code}</span>
            <span className="text-xs text-gray-400">{name}</span>
          </span>
        ))}
      </div>
    </div>

    {/* Category */}
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
        <code className="text-sm font-mono font-semibold text-gray-800">category</code>
        <span className="text-xs text-gray-500">- accepted values</span>
      </div>
      <div className="p-4 flex flex-wrap gap-2">
        {[
          { code: "bnd",   label: "Bonds",      color: "bg-purple-50 border-purple-200 text-purple-700" },
          { code: "bsnss", label: "Business",   color: "bg-blue-50 border-blue-200 text-blue-700" },
          { code: "cnsm",  label: "Consumer",   color: "bg-orange-50 border-orange-200 text-orange-700" },
          { code: "gdp",   label: "GDP",        color: "bg-green-50 border-green-200 text-green-700" },
          { code: "gov",   label: "Government", color: "bg-red-50 border-red-200 text-red-700" },
          { code: "hse",   label: "Housing",    color: "bg-yellow-50 border-yellow-200 text-yellow-700" },
          { code: "lbr",   label: "Labour",     color: "bg-teal-50 border-teal-200 text-teal-700" },
          { code: "mny",   label: "Money",      color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
          { code: "prce",  label: "Prices",     color: "bg-pink-50 border-pink-200 text-pink-700" },
          { code: "trd",   label: "Trade",      color: "bg-indigo-50 border-indigo-200 text-indigo-700" },
        ].map(({ code, label, color }) => (
          <span key={code} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm ${color}`}>
            <span className="font-mono font-semibold">{code}</span>
            <span className="text-xs opacity-75">·</span>
            <span className="text-xs">{label}</span>
          </span>
        ))}
      </div>
    </div>

  </div>
</div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Unique event identifier</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">title <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Event title/description</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">country <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">2-letter country code</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">indicator <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Economic indicator name</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ticker <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">Related financial ticker</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">comment <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">Additional commentary</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">category <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Event category (e.g., &quot;gov&quot;, &quot;fin&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">period <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Period covered (e.g., &quot;Q1&quot;, &quot;2025&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">referenceDate <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">Reference date for data</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">source <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Data source</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">sourceUrl <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Source URL</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">actual <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">Actual released value (formatted)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">previous <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">Previous period value (formatted)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">forecast <span className="text-gray-500">string|null</span></p>
                  <p className="text-sm text-gray-600">Forecast/consensus value (formatted)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">actualRaw <span className="text-gray-500">number|null</span></p>
                  <p className="text-sm text-gray-600">Actual value (numeric)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">previousRaw <span className="text-gray-500">number|null</span></p>
                  <p className="text-sm text-gray-600">Previous value (numeric)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">forecastRaw <span className="text-gray-500">number|null</span></p>
                  <p className="text-sm text-gray-600">Forecast value (numeric)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">currency <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Currency code</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">importance <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Importance level (-1 to 3)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">date <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Event date and time (ISO 8601)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Economic Calendar" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Economic Calendar</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="econ/calendar"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="04-macros-and-signals/calendar/economic-calendar"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
